import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MapPin, ShieldCheck, Star } from "lucide-react";
import { propertyService } from "@/lib/services";
import { PageShell } from "@/components/nav";
import { ListingCard } from "@/components/listing-card";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/properties/$propertyId")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData({
      queryKey: ["property", params.propertyId],
      queryFn: () => propertyService.get(params.propertyId),
    }),
  errorComponent: PropertyError,
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="text-3xl font-extrabold text-ink">Property not found</h1>
        <Link
          to="/properties"
          className="mt-4 inline-block font-semibold text-primary hover:underline"
        >
          ← Back to properties
        </Link>
      </div>
    </PageShell>
  ),
  component: PropertyDetail,
});

function PropertyError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <PageShell>
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="text-2xl font-extrabold text-ink">Couldn't load this property</h1>
        <p className="mt-2 text-sm text-ink-muted">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-6 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          Try again
        </button>
      </div>
    </PageShell>
  );
}

function PropertyDetail() {
  const { propertyId } = Route.useParams();
  const { data } = useQuery({
    queryKey: ["property", propertyId],
    queryFn: () => propertyService.get(propertyId),
  });
  const property = data?.data;
  if (!property) return null;
  // The headline rating is the server's aggregate over every review, not an
  // average of the page of reviews that happens to be loaded below.
  const avg = property.rating;
  const reviews = property.reviews ?? [];
  return (
    <PageShell>
      <div className="relative h-72 w-full overflow-hidden bg-muted md:h-96">
        {property.cover_image ? (
          <img
            src={property.cover_image}
            alt={property.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="grid h-full place-items-center text-ink-subtle">No cover image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      </div>
      <div className="mx-auto -mt-16 max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-elevated)] md:p-8">
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">
              {property.property_type_label}
            </span>
            {property.verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-accent-foreground">
                <ShieldCheck className="h-3 w-3" /> Verified
              </span>
            )}
            {avg > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-ink">
                <Star className="h-3 w-3 fill-accent text-accent" /> {avg.toFixed(1)}
              </span>
            )}
          </div>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink md:text-4xl">
            {property.name}
          </h1>
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-ink-muted">
            <MapPin className="h-4 w-4" /> {property.address}
          </p>
          <p className="mt-1 text-xs text-ink-subtle">Hosted by {property.host?.name}</p>
          <p className="mt-5 whitespace-pre-line leading-relaxed text-ink">
            {property.description}
          </p>
        </div>

        {property.listings && property.listings.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-2xl font-extrabold tracking-tight text-ink">
              Available listings
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {property.listings.map((l) => (
                <ListingCard key={l.id} listing={{ ...l, property }} />
              ))}
            </div>
          </section>
        )}

        <section className="my-10">
          <h2 className="mb-4 text-2xl font-extrabold tracking-tight text-ink">
            Reviews ({property.total_reviews})
          </h2>
          {reviews.length === 0 && (
            <div className="rounded-2xl border border-border bg-card p-8 text-center text-ink-muted">
              No reviews yet.
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-ink">{r.user?.name ?? "Guest"}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted">
                    <Star className="h-3.5 w-3.5 fill-accent text-accent" /> {r.rating}
                  </span>
                </div>
                <p className="mt-2 text-sm text-ink">{r.comment ?? "—"}</p>
                <p className="mt-2 text-xs text-ink-subtle">{formatDate(r.created_at)}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </PageShell>
  );
}
